/**
 * saveErrorHandler — Iter-213+ — Centralized error surfacing + draft backup
 * for save/PATCH/PUT calls across onboarding, profile, FIFA intake, SSN
 * forms, compliance, etc.
 *
 * What it solves (P0 production incident):
 *   1. Generic "Failed to save" toast hides the real backend error so users
 *      and support have no idea what went wrong.
 *   2. Mobile flakiness — a transient 502 / 504 / network blip lost all the
 *      data the user just typed.
 *
 * 🛡 Iter-281 (2026-06-23) — P0 DATA INTEGRITY FIX
 * --------------------------------------------------
 * Admin reported drafts from talent A bleeding into talent B's edit form.
 * Root cause: drafts were keyed by a static `scope` string (e.g.
 * 'profile.basic'), so two different talents loaded in the same browser
 * — whether by admin impersonation or normal account-switching — shared
 * the same key.
 *
 * Fix: introduce `draftScope({ section, entityId, viewerId })` which
 * produces a fully scoped key of the form:
 *
 *     save-backup:v2:viewer=<currentUserId>:entity=<entityId>:section=<section>
 *
 *   - `viewer` is the currently logged-in user (taken from localStorage:user)
 *   - `entity` is the talent / profile being EDITED (defaults to viewer
 *     for self-edits; must be passed explicitly for any path where one
 *     user can edit another's record — admin impersonation, support tools)
 *   - `section` is the form section the draft belongs to
 *
 * Backwards-compat: `saveDraft / loadDraft / clearDraft` still accept a
 * plain scope string, but for safety they REFUSE to read or write unless
 * the scope starts with `v2:`. Pass through `draftScope(...)` to get a
 * compliant key. Any legacy non-v2 keys still in localStorage are wiped
 * by `purgeLegacyDrafts()` (called from App.js on every boot).
 *
 * Usage:
 *   import {
 *     handleSaveError, saveDraft, loadDraft, clearDraft, draftScope,
 *   } from '../utils/saveErrorHandler';
 *
 *   const scope = draftScope({ section: 'profile.basic', entityId: loaded.user_id });
 *
 *   try {
 *     saveDraft(scope, payload);
 *     await api.patch('/profile-info/nickname', payload);
 *     clearDraft(scope);
 *     toast.success('Saved');
 *   } catch (err) {
 *     const info = handleSaveError(err, { scope, endpoint, payload });
 *     toast.error(info.message);
 *   }
 */

const DRAFT_PREFIX = 'save-backup:';        // legacy prefix (pre Iter-281)
const DRAFT_PREFIX_V2 = 'save-backup:v2:';  // properly scoped prefix
const MAX_DRAFT_KEYS = 64;
const MAX_DRAFT_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/** Read the current viewer (logged-in user) id from localStorage. Falls
 * back to `'anon'` if the user blob is missing so the key still works. */
const currentViewerId = () => {
  if (typeof window === 'undefined') return 'anon';
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return 'anon';
    const u = JSON.parse(raw);
    return u?.id || u?.user_id || u?.email || 'anon';
  } catch {
    return 'anon';
  }
};

/**
 * Build a fully scoped draft key. Use this from EVERY caller — saveDraft /
 * loadDraft / clearDraft refuse to operate on unscoped keys.
 *
 * @param {object} args
 *   - section:  logical form id, e.g. 'profile.basic'  (required)
 *   - entityId: id of the talent/profile being edited (defaults to viewer)
 *   - viewerId: override for the current viewer id (rarely needed)
 *
 * @returns {string} scoped key safe to pass to saveDraft / loadDraft
 *
 * Iter-284 — Cross-profile draft contamination kill switch.
 * If the viewer is NOT editing their own record (i.e. an admin / staff
 * member is editing another user's profile, including impersonation),
 * we return the non-persistable sentinel `__INVALID_DRAFT_SCOPE__`.
 * That guarantees saveDraft / loadDraft refuse to store or restore
 * ANY draft from an admin-cross-profile session, eliminating the
 * "previous profile's data restored on the next profile" bug entirely.
 * Talents editing their own profile remain unaffected.
 */
export const draftScope = ({ section, entityId, viewerId } = {}) => {
  if (!section) {
    // Defensive — without a section we can't even namespace properly,
    // so callers must supply one. Return a non-persistable sentinel.
    return '__INVALID_DRAFT_SCOPE__';
  }
  const v = viewerId || currentViewerId();
  const e = entityId == null || entityId === '' ? v : String(entityId);
  // Iter-284 — if viewer is editing a DIFFERENT entity (admin-cross-profile),
  // refuse to persist any draft. This is the deliberate, user-requested
  // kill switch so admins can't accidentally carry edits between profiles.
  if (String(v) !== String(e)) {
    return '__INVALID_DRAFT_SCOPE__';
  }
  return `v2:viewer=${v}:entity=${e}:section=${section}`;
};

/** True iff `scope` looks like a v2 scoped key. */
const isScoped = (scope) => typeof scope === 'string' && scope.startsWith('v2:');

/** Generate a short correlation id so the same error can be matched
 * across the user-visible toast, the browser console, and (eventually)
 * the backend logs / Sentry. */
const newCorrelationId = () => {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 8);
  return `e1${t}${r}`;
};

/** Robustly extract a human-readable message from an axios/FastAPI error. */
const extractMessage = (err) => {
  if (!err) return 'Unknown error';
  if (err.code === 'ERR_NETWORK' || !err.response) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'You appear to be offline. Your changes are saved locally and will sync when you reconnect.';
    }
    return 'Network error — please check your connection and try again.';
  }
  const status = err.response?.status;
  const data = err.response?.data;
  if (status === 401) return 'Your session expired. Please sign in again.';
  if (status === 403) return data?.detail || 'You do not have permission to do this.';
  if (status === 404) return data?.detail || 'That record no longer exists.';
  if (status === 409) return data?.detail || 'A conflict occurred — refresh and try again.';
  if (status === 413) return 'The file is too large.';
  if (status === 429) return 'Too many attempts — please wait a few seconds.';
  if (status >= 500) return data?.detail || `Server error (${status}) — please try again.`;
  if (Array.isArray(data?.detail)) {
    const first = data.detail[0];
    if (first?.msg) {
      const field = (first.loc || []).filter((l) => l !== 'body').join('.');
      return field ? `${field}: ${first.msg}` : first.msg;
    }
  }
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data === 'string') return data;
  return err.message || 'Could not save. Please try again.';
};

/**
 * Save a draft of the user's payload to localStorage. Will refuse to
 * persist if `scope` is not a v2 scoped key (call `draftScope(...)` to
 * build one).
 */
export const saveDraft = (scope, payload) => {
  if (typeof window === 'undefined') return;
  // Iter-284 — admin-cross-profile sentinel: silently no-op (don't log).
  if (scope === '__INVALID_DRAFT_SCOPE__') return;
  if (!isScoped(scope)) {
    // eslint-disable-next-line no-console
    console.warn('[saveDraft] refusing unscoped key — wrap with draftScope({section, entityId})', scope);
    return;
  }
  try {
    const key = DRAFT_PREFIX_V2 + scope.slice(3); // strip leading 'v2:' (already in prefix)
    const wrapper = { ts: Date.now(), payload };
    window.localStorage.setItem(key, JSON.stringify(wrapper));
    pruneOldDrafts();
  } catch (_) { /* quota / private mode — ignore */ }
};

/** Read the saved draft back. Returns the original `payload` or `null`. */
export const loadDraft = (scope) => {
  if (typeof window === 'undefined') return null;
  // Iter-284 — admin-cross-profile sentinel: silently return null.
  if (scope === '__INVALID_DRAFT_SCOPE__') return null;
  if (!isScoped(scope)) {
    // eslint-disable-next-line no-console
    console.warn('[loadDraft] refusing unscoped key — wrap with draftScope({section, entityId})', scope);
    return null;
  }
  try {
    const key = DRAFT_PREFIX_V2 + scope.slice(3);
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const wrapper = JSON.parse(raw);
    if (!wrapper || typeof wrapper !== 'object') return null;
    if (Date.now() - (wrapper.ts || 0) > MAX_DRAFT_AGE_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return wrapper.payload;
  } catch (_) {
    return null;
  }
};

/** Clear the draft once the upstream save succeeded. */
export const clearDraft = (scope) => {
  if (typeof window === 'undefined') return;
  if (!isScoped(scope)) return; // silent — clearing an unscoped key is a no-op
  try {
    window.localStorage.removeItem(DRAFT_PREFIX_V2 + scope.slice(3));
  } catch (_) { /* ignore */ }
};

/**
 * One-time-per-load purge of legacy (pre-Iter-281) draft keys. Called
 * from App.js on boot. Removes every `save-backup:*` key that is NOT in
 * v2 format so stale drafts from before this fix can't leak across
 * profiles. Idempotent.
 */
export const purgeLegacyDrafts = () => {
  if (typeof window === 'undefined') return 0;
  try {
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(DRAFT_PREFIX) && !k.startsWith(DRAFT_PREFIX_V2)) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
    if (toRemove.length) {
      // eslint-disable-next-line no-console
      console.info(`[saveErrorHandler] purged ${toRemove.length} legacy draft key(s)`);
    }
    return toRemove.length;
  } catch (_) {
    return 0;
  }
};

/** Trim stored drafts so quota doesn't fill up over time. */
const pruneOldDrafts = () => {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(DRAFT_PREFIX_V2)) keys.push(k);
    }
    if (keys.length <= MAX_DRAFT_KEYS) return;
    const dated = keys.map((k) => {
      try { return { k, ts: JSON.parse(window.localStorage.getItem(k))?.ts || 0 }; }
      catch { return { k, ts: 0 }; }
    }).sort((a, b) => a.ts - b.ts);
    for (let i = 0; i < dated.length - MAX_DRAFT_KEYS; i++) {
      window.localStorage.removeItem(dated[i].k);
    }
  } catch (_) { /* ignore */ }
};

/**
 * Centralized error handler — call from any catch block.
 *
 * @param {Error} err     - axios/network error
 * @param {object} opts
 *   - scope:    string  - logical form id, e.g. "profile.basic" / "fifa.intake"
 *   - endpoint: string  - the URL that failed (for logging)
 *   - payload:  any     - the payload that was being saved (becomes draft backup)
 *   - silent:   boolean - if true, skip toast (caller will render its own UI)
 *
 * @returns {{ message: string, status: number|null, correlationId: string, isOffline: boolean, raw: any }}
 */
export const handleSaveError = (err, opts = {}) => {
  const { scope, endpoint, payload, silent = false } = opts;
  const correlationId = newCorrelationId();
  const message = extractMessage(err);
  const status = err?.response?.status || null;
  const isOffline = (typeof navigator !== 'undefined' && navigator.onLine === false)
    || err?.code === 'ERR_NETWORK';

  // Persist the unsent payload so a refresh / crash doesn't wipe data.
  // Only does anything if the caller passed a v2 scoped key.
  if (scope && payload !== undefined) {
    saveDraft(scope, payload);
  }

  /* eslint-disable no-console */
  try {
    console.groupCollapsed(`%c[save-error] ${correlationId} · ${endpoint || '(unknown endpoint)'}`,
      'color:#ef4444;font-weight:bold');
    console.log('status:', status);
    console.log('message:', message);
    console.log('isOffline:', isOffline);
    console.log('scope:', scope || '(none)');
    console.log('payload:', payload);
    console.log('response.data:', err?.response?.data);
    console.log('response.headers:', err?.response?.headers);
    console.log('request URL:', err?.config?.url);
    console.log('request method:', err?.config?.method);
    console.log('full error:', err);
    console.groupEnd();
  } catch (_) { /* ignore console issues */ }
  /* eslint-enable no-console */

  return {
    message: silent ? message : `${message} (ref ${correlationId})`,
    status,
    correlationId,
    isOffline,
    raw: err,
  };
};

export default handleSaveError;
